import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

/** A plugin someone tried to add while the template already holds a version of it. */
export interface DuplicatePlugin {
  pluginId: string;
  /** The version the template holds. */
  held: string;
  /** The version that was not added. */
  wanted: string;
  /** Why the held one cannot come off, when it cannot (a saved template keeps its plugins). */
  removalBlockedBy: string | null;
}

interface DuplicatePluginDialogProps {
  duplicate: DuplicatePlugin | null;
  onClose: () => void;
}

/** A template holds one version of a plugin; adding another says so instead of doing nothing. */
const DuplicatePluginDialog = ({ duplicate, onClose }: DuplicatePluginDialogProps) => (
  <Dialog open={duplicate !== null} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderTop: 6, borderColor: 'error.main' } }}>
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
      <ErrorOutlineIcon color="error" />
      This plugin is already in the template
    </DialogTitle>
    {duplicate && (
      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}>
          {duplicate.pluginId} {duplicate.wanted} was not added: the template already holds {duplicate.pluginId} {duplicate.held}, and a template holds one version of each plugin.
        </Alert>
        <DialogContentText sx={{ color: 'text.primary' }}>
          {duplicate.removalBlockedBy
            ? 'A saved template keeps its plugins, so its version cannot change here. Start a new template from this one, remove the version it holds there, then add the one you want.'
            : duplicate.held === duplicate.wanted
            ? 'Nothing to add: this version is already in the template.'
            : `To use ${duplicate.wanted}, remove ${duplicate.held} first: pick ${duplicate.held} on the plugin's row and press −, then pick ${duplicate.wanted} and press +.`}
        </DialogContentText>
      </DialogContent>
    )}
    <DialogActions>
      <Button variant="contained" onClick={onClose} autoFocus>
        Back to the template
      </Button>
    </DialogActions>
  </Dialog>
);

export default DuplicatePluginDialog;
